// Vista pública de RESUMEN de un Reclamo de Terceros.
// El link del correo de confirmación redirige aquí con ?token=<id> (UUID del reclamo).
// Muestra, en solo lectura, toda la información declarada por el cliente, agrupada por sección.
// Solo se renderizan los campos declarados (whitelist): NO se exponen columnas internas del admin.

import { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { Button, Result, Row, Spin, Tag } from 'antd';
import { useFormContext } from '../../Context/DeclarationContext';
import { getThirdPartyClaimById, getThirdPartyClaimDocumentBase64 } from '../../Services/thirdPartyClaims';
import { base64ToBlob, isImageFile } from './documentViewer';
import { HOME_ROUTE } from '../../Components/Router/constant';
import gamaLogo from '../../Assets/images/gamamobility-logo-hz-white.svg';
import './ThirdPartyClaimSummary.scss';

const ROLE_LABELS: Record<string, string> = {
  CIA_SEGUROS: 'Compañía de Seguros del tercero',
  ABOGADO: 'Abogado del tercero',
  TERCERO: 'Tercero involucrado',
};
const MEDIA_LABELS: Record<string, string> = {
  FOTOS: 'Solo fotografías',
  VIDEOS: 'Solo videos',
  AMBOS: 'Fotografías y videos',
  NINGUNO: 'No cuenta con material audiovisual',
};
const PROPERTY_LABELS: Record<string, string> = {
  VEHICULO: 'Vehículo',
  OTRO: 'Otro bien',
};
const STATUS_LABELS: Record<string, string> = {
  PENDING: 'Pendiente',
  IN_PROGRESS: 'En proceso',
  CLOSED: 'Cerrado',
};

// Fechas-calendario (accidentDate es columna `date`, sin hora). Se formatea con
// moment.parseZone para NO convertir a la zona local: `new Date("2026-07-01")` es
// medianoche UTC y con getters locales (Chile UTC-4) retrocedía un día. Alineado
// con el estándar de Siniestro (Dashboard usa moment/moment.parseZone).
const fmtDate = (value?: string | null): string => (value ? moment.parseZone(value).format('DD-MM-YYYY') : '');
const fmtMoney = (value?: number | null): string =>
  value === null || value === undefined ? '' : `$${Number(value).toLocaleString('es-CL')}`;
const yesNo = (value?: boolean | null): string =>
  value === true ? 'Sí' : value === false ? 'No' : '';
const idLabel = (idType?: string) => (idType === 'PASAPORTE' ? 'N° Pasaporte' : 'RUT');

// Cada campo es una fila etiqueta/valor a ancho completo, en una sola columna. Se copia
// la estructura del PDF descargable ("Anexo A"): barra de sección + grilla etiqueta(35%)/
// valor(65%). Más clara que la grilla de 2 columnas anterior. `span` ya no aplica.
type Field = { label: string; value?: any; span?: number };

const isFilled = (v: any) => v !== undefined && v !== null && v !== '';

const SummarySection = ({ title, fields }: { title: string; fields: Field[] }) => {
  const rows = fields.filter((f) => isFilled(f.value));
  if (rows.length === 0) return null;
  return (
    <section className="tpc-summary__table">
      <div className="tpc-summary__bar">{title}</div>
      {rows.map((f) => (
        <div className="tpc-summary__row" key={f.label}>
          <div className="tpc-summary__th">{f.label}</div>
          <div className="tpc-summary__td">{f.value}</div>
        </div>
      ))}
    </section>
  );
};

type DocAsset = { blobUrl: string; isImage: boolean };

// Sección "Documentos adjuntos": muestra los adjuntos declarados y permite al cliente
// visualizarlos/descargarlos. Cada documento se trae en base64 (endpoint público
// /documents/:id/view), se reconstruye como Blob y se expone vía objectURL; las imágenes
// se muestran además como miniatura clickeable. Los objectURL se liberan al desmontar.
const AttachmentsSection = ({ claimId, documents }: { claimId: string; documents: any[] }) => {
  const [assets, setAssets] = useState<Record<string, DocAsset>>({});
  const urlsRef = useRef<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const map: Record<string, DocAsset> = {};
      await Promise.all(
        documents
          .filter((doc) => doc?.id)
          .map(async (doc) => {
            try {
              const { data } = await getThirdPartyClaimDocumentBase64(claimId, doc.id);
              if (!data?.buffer) return;
              const blobUrl = URL.createObjectURL(base64ToBlob(data.buffer, doc.filename, data.contentType));
              urlsRef.current.push(blobUrl);
              map[doc.id] = { blobUrl, isImage: isImageFile(doc.filename, data.contentType) };
            } catch {
              // Si un documento no se puede cargar, se deja su nombre sin enlace.
            }
          }),
      );
      if (!cancelled) setAssets(map);
    })();
    return () => {
      cancelled = true;
    };
  }, [claimId, documents]);

  // Libera los objectURL creados al desmontar la vista.
  useEffect(() => () => urlsRef.current.forEach((url) => URL.revokeObjectURL(url)), []);

  return (
    <section className="tpc-summary__table">
      <div className="tpc-summary__bar">Documentos adjuntos</div>
      {documents.map((doc: any) => {
        const asset = doc?.id ? assets[doc.id] : undefined;
        return (
          <div className="tpc-summary__row" key={doc.id || doc.documentName}>
            <div className="tpc-summary__th">
              {asset ? (
                <a href={asset.blobUrl} target="_blank" rel="noopener noreferrer" className="tpc-summary__doc-link">
                  {doc.documentName}
                </a>
              ) : (
                doc.documentName
              )}
            </div>
            <div className="tpc-summary__td">
              {asset ? (
                asset.isImage ? (
                  <a
                    href={asset.blobUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="tpc-summary__doc-thumb"
                  >
                    <img src={asset.blobUrl} alt={doc.documentName} />
                  </a>
                ) : (
                  <a href={asset.blobUrl} target="_blank" rel="noopener noreferrer" className="tpc-summary__doc-link">
                    Ver / Descargar
                  </a>
                )
              ) : (
                doc.filename || 'Ingresado'
              )}
            </div>
          </div>
        );
      })}
    </section>
  );
};

export const ThirdPartyClaimSummary = () => {
  const navigate = useNavigate();
  const { dispatch } = useFormContext();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<'ok' | 'missing' | 'notfound' | 'error'>('ok');
  const [claim, setClaim] = useState<any>(null);

  useEffect(() => {
    dispatch({ type: 'SET_HEADER_TITLE', payload: 'Resumen del Reclamo de Terceros' });
  }, [dispatch]);

  useEffect(() => {
    const id = new URLSearchParams(window.location.search).get('token') || '';
    if (!id) {
      setStatus('missing');
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const { data } = await getThirdPartyClaimById(id);
        if (!data || !data.id) {
          setStatus('notfound');
        } else {
          setClaim(data);
        }
      } catch (error) {
        setStatus((error as any)?.status === 404 ? 'notfound' : 'error');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const goHome = () => {
    window.scrollTo(0, 0);
    navigate(HOME_ROUTE);
  };

  if (loading) {
    return <Spin spinning fullscreen tip="Cargando resumen del reclamo..." />;
  }

  if (status !== 'ok' || !claim) {
    const map = {
      missing: {
        status: 'warning' as const,
        title: 'Falta el identificador del reclamo',
        subTitle: 'Abra el enlace desde el correo de confirmación que recibió al declarar su reclamo.',
      },
      notfound: {
        status: '404' as const,
        title: 'No encontramos el reclamo',
        subTitle: 'Verifique el enlace de su correo e intente nuevamente.',
      },
      error: {
        status: 'error' as const,
        title: 'No pudimos cargar el resumen',
        subTitle: 'Ocurrió un problema al obtener la información. Intente más tarde.',
      },
    };
    const cfg = map[status === 'ok' ? 'error' : status];
    return (
      <div className="tpc-summary">
        <Result
          status={cfg.status}
          title={cfg.title}
          subTitle={cfg.subTitle}
          extra={
            <Button type="primary" onClick={goHome}>
              Volver al inicio
            </Button>
          }
        />
      </div>
    );
  }

  const isVehicle = claim.affectedPropertyType === 'VEHICULO';

  return (
    <div className="tpc-summary">
      <div className="tpc-summary__inner">
        <div className="tpc-summary__head">
          <div className="tpc-summary__band">
            <div>
              <div className="tpc-summary__band-title">Reclamo de Terceros</div>
              <div className="tpc-summary__band-id">N° de seguimiento: {claim.shortId}</div>
            </div>
            <img src={gamaLogo} alt="Gama Mobility" className="tpc-summary__band-logo" />
          </div>
          <div className="tpc-summary__subhead">
            {claim.status && (
              <Tag color="processing">{STATUS_LABELS[claim.status] || claim.status}</Tag>
            )}
            <span className="tpc-summary__lead">Este es el resumen de la información que usted declaró.</span>
          </div>
        </div>

        <SummarySection
          title="Datos del siniestro"
          fields={[
            { label: 'Su rol en el proceso', value: ROLE_LABELS[claim.claimantRole] || claim.claimantRole },
            { label: 'Patente vehículo Gama', value: claim.gamaVehiclePlate },
            { label: 'Conductor Gama', value: claim.gamaDriverName },
            { label: `${idLabel(claim.driverIdType)} conductor Gama`, value: claim.gamaDriverId },
            { label: 'Fecha del siniestro', value: fmtDate(claim.accidentDate) },
            { label: 'Hora del siniestro', value: claim.accidentTime },
            { label: 'Región del siniestro', value: claim.accidentRegion },
            { label: 'Comuna del siniestro', value: claim.accidentCommune },
            { label: 'Lugar del siniestro', value: claim.accidentLocation, span: 2 },
            { label: 'Relato del siniestro', value: claim.accidentDescription, span: 2 },
          ]}
        />

        <SummarySection
          title="Datos de contacto del tercero o representante"
          fields={[
            { label: 'Nombre completo', value: claim.claimantFullName },
            { label: idLabel(claim.claimantIdType), value: claim.claimantId },
            { label: 'Correo(s)', value: (claim.claimantEmails || []).join(', '), span: 2 },
            { label: 'Teléfono', value: claim.claimantPhone },
            { label: 'Región', value: claim.thirdPartyRegion },
            { label: 'Comuna', value: claim.thirdPartyCommune },
            { label: 'Institución', value: claim.institutionName },
            { label: 'RUT institución', value: claim.institutionRut },
          ]}
        />

        <SummarySection
          title="Información del bien afectado"
          fields={[
            { label: 'Tipo de bien', value: PROPERTY_LABELS[claim.affectedPropertyType] || claim.affectedPropertyType },
            ...(isVehicle
              ? [
                  { label: 'Dueño del bien', value: claim.thirdPartyOwnerName },
                  { label: 'RUT del dueño', value: claim.thirdPartyOwnerId },
                  { label: 'Tipo de vehículo', value: claim.thirdPartyVehicleType },
                  { label: 'Patente', value: claim.thirdPartyVehiclePlate },
                  { label: 'Marca', value: claim.thirdPartyVehicleBrand },
                  { label: 'Modelo', value: claim.thirdPartyVehicleModel },
                  { label: 'Año', value: claim.thirdPartyVehicleYear },
                  { label: 'Material audiovisual de Gama', value: MEDIA_LABELS[claim.gamaMediaType] || claim.gamaMediaType },
                  { label: '¿El dueño cuenta con seguro?', value: yesNo(claim.thirdPartyHasInsurance) },
                  { label: 'N° siniestro Cía. de Seguros', value: claim.insuranceClaimNumber },
                  { label: 'Nombre Cía. de Seguros', value: claim.insuranceCompanyName },
                  { label: '¿Hubo lesiones corporales?', value: yesNo(claim.hasBodilyInjuries) },
                  { label: '¿Se realizó alcoholemia?', value: yesNo(claim.hasAlcoholTest) },
                  { label: 'Partes o piezas dañadas', value: claim.damagedParts, span: 2 },
                  { label: '¿Tiene presupuesto de reparación?', value: yesNo(claim.hasRepairBudget) },
                ]
              : [
                  { label: 'Tipo de bien y/o lesión física', value: claim.damagedGoods, span: 2 },
                  { label: 'Detalle del daño', value: claim.damageInfo, span: 2 },
                ]),
            { label: 'Valor a reclamar', value: fmtMoney(claim.claimedAmount) },
          ]}
        />

        {Array.isArray(claim.documents) && claim.documents.length > 0 && (
          <AttachmentsSection claimId={claim.id} documents={claim.documents} />
        )}

        <Row justify="center" className="tpc-summary__actions">
          <Button type="primary" onClick={goHome}>
            Volver al inicio
          </Button>
        </Row>
      </div>
    </div>
  );
};

export default ThirdPartyClaimSummary;
